import React from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SchoolIcon from '@mui/icons-material/School';
import WorkIcon from '@mui/icons-material/Work';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PaidIcon from '@mui/icons-material/Paid';
import PeopleIcon from '@mui/icons-material/People';
import FileUploadIcon from '@mui/icons-material/FileUpload';
// import FileDownloadIcon from '@mui/icons-material/FileDownload';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

const drawerWidth = 260;

interface MainLayoutProps {
  children: React.ReactNode;
}

const navItems = [
  { label: 'Dashboard', icon: <DashboardIcon />, to: '/dashboard' },
  { label: 'Candidatures', icon: <AssignmentIndIcon />, to: '/candidatures' },
  { label: 'Postes', icon: <WorkIcon />, to: '/posts' },
  { label: 'Écoles', icon: <SchoolIcon />, to: '/schools' },
  { label: 'Activités', icon: <ReceiptLongIcon />, to: '/activities' },
  { label: 'Paiements', icon: <PaidIcon />, to: '/payments' },
  { label: 'Utilisateurs', icon: <PeopleIcon />, to: '/users' },
  { label: 'Imports', icon: <FileUploadIcon />, to: '/imports' },
  // { label: 'Exports', icon: <FileDownloadIcon />, to: '/exports' },
  { label: 'Rapports', icon: <BarChartIcon />, to: '/reports' },
  { label: 'Paramètres', icon: <SettingsIcon />, to: '/settings' },
];

export const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const { pathname } = useLocation();
  const { signOutUser, displayName, role } = useAuth();
  const visibleNavItems = React.useMemo(() => {
    if (role === 'cap' || role === 'ae') {
      const allowed = [
        '/dashboard',
        '/candidatures',
        '/posts',
        '/schools',
        '/activities',
        '/payments',
      ];
      return navItems.filter((item) => allowed.includes(item.to));
    }
    if (role !== 'admin') {
      return navItems.filter((item) => item.to !== '/imports');
    }
    return navItems;
  }, [role]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <div>
      <Toolbar sx={{ px: 3 }}>
        <Typography variant="h6" noWrap component="div">
          Kalan Admin
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {visibleNavItems.map((item) => (
          <ListItem key={item.to} disablePadding>
            <ListItemButton
              component={NavLink}
              to={item.to}
              selected={pathname.startsWith(item.to)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap sx={{ flexGrow: 1 }}>
            Backoffice Kalan
          </Typography>
          <Box sx={{ mr: 2, textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
            <Typography variant="body2">
              {displayName || 'Utilisateur'}
            </Typography>
            <Typography variant="caption" color="inherit">
              {role === 'admin'
                ? 'Administrateur'
                : role === 'cap'
                ? 'CAP'
                : role === 'ae'
                ? 'AE'
                : role}
            </Typography>
          </Box>
          <IconButton color="inherit" onClick={() => signOutUser()}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="navigation principale"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          minHeight: '100vh',
          backgroundColor: (theme) => theme.palette.background.default,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

